import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PageHeader, LoadingState, EmptyState, ErrorState } from "@/components/features/states";
import { TrustBadge } from "@/components/features/badges";
import { formatRelativeTime } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, Check, X, ZoomIn, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

export function AdminVerifications() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState<any | null>(null);
  const [reason, setReason] = useState("");
  const [zoomImg, setZoomImg] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "verifications"],
    queryFn: () => api.admin.verifications(),
    refetchInterval: 10000,
  });
  const items = data?.data?.items ?? [];

  const approve = async (id: string) => {
    const res = await api.admin.reviewVerification(id, { action: "approve" });
    if (!res.ok) return toast.error(res.error);
    toast.success(t("admin.verifications.idVerifiedToast"));
    qc.invalidateQueries({ queryKey: ["admin", "verifications"] });
    qc.invalidateQueries({ queryKey: ["admin", "stats"] });
  };

  const doReject = async () => {
    if (!rejecting || !reason.trim()) return toast.error(t("admin.verifications.enterReason"));
    const res = await api.admin.reviewVerification(rejecting.id, { action: "reject", reason: reason.trim() });
    if (!res.ok) return toast.error(res.error);
    toast.success(t("admin.verifications.verificationRejectedToast"));
    setRejecting(null);
    setReason("");
    qc.invalidateQueries({ queryKey: ["admin", "verifications"] });
    qc.invalidateQueries({ queryKey: ["admin", "stats"] });
  };

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.verifications.loadFailed")} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <PageHeader title={t("admin.verifications.title")} subtitle={t("admin.verifications.subtitle", { count: items.length })} />

      {items.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck className="h-8 w-8" />}
          title={t("admin.verifications.empty")}
          description={t("admin.verifications.emptyDesc")}
        />
      ) : (
        <div className="space-y-3">
          {items.map((item: any) => (
            <Card key={item.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
                      {item.user.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold">{item.user.name}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {item.user.email}</span>
                      {item.user.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {item.user.phone}</span>}
                    </div>
                  </div>
                  <TrustBadge status={item.status} />
                </div>

                <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-3 text-sm sm:grid-cols-3">
                  <div><span className="text-muted-foreground">{t("admin.verifications.idTypeLabel")}</span> <span className="font-medium capitalize">{item.idType?.replace("_", " ")}</span></div>
                  <div><span className="text-muted-foreground">{t("admin.verifications.idNumberLabel")}</span> <span className="font-mono font-medium">{item.idNumber}</span></div>
                  <div><span className="text-muted-foreground">{t("admin.verifications.submittedLabel")}</span> <span className="font-medium">{item.submittedAt ? formatRelativeTime(item.submittedAt) : "—"}</span></div>
                </div>

                {(item.idFrontUrl || item.idBackUrl || item.selfieUrl) && (
                  <div className="grid grid-cols-3 gap-2">
                    {item.idFrontUrl && <DocThumb label={t("admin.verifications.front")} url={item.idFrontUrl} onClick={() => setZoomImg(item.idFrontUrl)} />}
                    {item.idBackUrl && <DocThumb label={t("admin.verifications.back")} url={item.idBackUrl} onClick={() => setZoomImg(item.idBackUrl)} />}
                    {item.selfieUrl && <DocThumb label={t("admin.verifications.selfie")} url={item.selfieUrl} onClick={() => setZoomImg(item.selfieUrl)} />}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button className="flex-1" onClick={() => approve(item.id)}>
                    <Check className="mr-1 h-4 w-4" /> {t("admin.verifications.approve")}
                  </Button>
                  <Button variant="outline" className="flex-1 text-destructive" onClick={() => setRejecting(item)}>
                    <X className="mr-1 h-4 w-4" /> {t("admin.verifications.reject")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.verifications.rejectVerification")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reason">{t("admin.verifications.reasonForRejection")}</Label>
            <Input id="reason" placeholder={t("admin.verifications.reasonPlaceholder")} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={doReject}>{t("admin.verifications.reject")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image zoom */}
      <Dialog open={!!zoomImg} onOpenChange={(o) => !o && setZoomImg(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("admin.verifications.document")}</DialogTitle>
          </DialogHeader>
          {zoomImg && <img src={zoomImg} alt={t("admin.verifications.documentAlt")} className="max-h-[70vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocThumb({ label, url, onClick }: { label: string; url: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="group relative overflow-hidden rounded-lg border border-border">
      <img src={url} alt={label} className="h-24 w-full object-cover" />
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100">
        <ZoomIn className="h-5 w-5 text-white" />
      </div>
      <span className="absolute bottom-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">{label}</span>
    </button>
  );
}
